import React, { useRef } from 'react';
import { Calendar } from 'lucide-react';

/**
 * DateInput — native type="date" input with a custom calendar icon trigger.
 * On mobile, tapping the icon opens the OS-native date picker.
 * On desktop, the browser's built-in date picker is used.
 *
 * Props:
 *   value    — YYYY-MM-DD string (controlled)
 *   onChange — (yyyymmdd: string) => void
 *   min      — YYYY-MM-DD min date (optional)
 *   max      — YYYY-MM-DD max date (optional)
 *   className — applied to the date input
 *   required  — passed through to the input
 */
const DateInput = ({ value, onChange, min, max, className = '', required }) => {
  const inputRef = useRef(null);

  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') { el.showPicker(); return; }
    } catch {}
    el.focus();
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="date"
        value={value || ''}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className={`${className} pr-9 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:w-0`}
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Open date picker"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
      >
        <Calendar className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DateInput;
